/* eslint-disable complexity */
/* eslint-disable no-console */
import React, {lazy, Suspense, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Routes, Route, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {Button, Result, Spin, App} from 'antd';
import {flowResult} from 'mobx';
import MainLayout from '@/layouts/MainLayout';
import SiderLayout from '@/layouts/SiderLayout';
import {initRem} from '@/utils/rem';
import {extractValue} from '@/utils';
import {rootStore} from '@/stores';
import {messageManager} from '@/utils/errorHandler/MessageManager';
import {handleUnauthorized} from '@/utils/auth/handleUnauthorized';
import NoauthSvg from '@/assets/icons/noauth.svg';
import {singleTabGuard} from '@/utils/singleTabGuard';
import {MigrationPhase, redirectToNewPlatform} from '@/utils/migration';
import styles from './index.module.less';

const InitialComponent = lazy(() => import('@/pages/InitialPage'));
const TaskRunningComponent = lazy(() => import('@/pages/TaskRunningPage'));
const LandingComponent = lazy(() => import('@/pages/LandingPage'));
const WhitelistComponent = lazy(() => import('@/pages/WhitelistPage'));
const BillingComponent = lazy(() => import('@/pages/BillingPage'));
const EnterpriseAdminComponent = lazy(() => import('@/features/enterprise/EnterpriseAdminMicroApp'));

type AuthState = 'checking' | 'authed' | 'no-auth' | 'no-whitelist' | 'sub-account-forbidden';

const SUB_ACCOUNT_403_MESSAGES: Record<string, string> = {
    ErrSubAccountNotManaged: '您的账号尚未被企业纳管，请联系企业管理员。',
    ErrSubAccountAccessDisabled: '您的账号访问权限已被企业管理员关闭，请联系管理员开启。',
    ErrSubAccountIAMDeleted: '您的子账号已被删除，请联系企业管理员。',
    ErrSubAccountIAMPending: '您的子账号尚未激活，请前往百度智能云控制台激活后重试。'
};

const AppWrapper = observer(() => {
    const {message, modal, notification} = App.useApp();
    const [authState, setAuthState] = useState<AuthState>('checking');
    const [subAccountErrCode, setSubAccountErrCode] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams();

    const [templateJobLoading, setTemplateJobLoading] = useState<boolean>(false);

    // 监听任意接口的企业子账号 403 事件，全局切换到无权限提示页
    useEffect(
        () => {
            const handler = (e: Event) => {
                const {code} = (e as CustomEvent<{code: string}>).detail;
                setSubAccountErrCode(code);
                setAuthState('sub-account-forbidden');
            };
            window.addEventListener('sub-account-forbidden', handler);
            return () => window.removeEventListener('sub-account-forbidden', handler);
        },
        []
    );

    // 从 loginstate 中提取 template_id invitecode 2.直接invitecode参数优先级更高
    const loginstate = searchParams.get('loginstate');
    const invitecodeVal = searchParams.get('invitecode');

    const template_id = extractValue(loginstate, 'templateid') || null;
    const invitecode = invitecodeVal || extractValue(loginstate, 'invitecode') || undefined;

    // 统一权限检查 - 在源头解决所有权限问题
    useEffect(
        () => {
            initRem();

            const checkAuth = async () => {
                try {
                    // 首先检查后端登录态；未登录则尝试 SSO ticket 兜底登录
                    const isLogin = await flowResult(rootStore.userStore.checkLoginStatus());
                    if (!isLogin) {
                        const ssoLoginSuccess = await flowResult(rootStore.userStore.trySsoLogin());
                        if (!ssoLoginSuccess) {
                            setAuthState('no-auth');
                            return;
                        }
                    }

                    // 拉取用户信息
                    const userInfo = await flowResult(rootStore.userStore.fetchUserInfo({invitecode}));
                    // 暂时隐藏apiKey
                    await flowResult(rootStore.userStore.fetchApikeyInfo());

                    // 在源头判断,一次性解决所有权限问题
                    if (!userInfo || !userInfo.user_id) {
                        setAuthState('no-auth');
                        return;
                    }

                    // 管理平台已完全关停：兜底重定向到新平台（正常情况下由服务端 302）
                    if (rootStore.userStore.migrationPhase === MigrationPhase.SHUTDOWN) {
                        redirectToNewPlatform();
                        return;
                    }

                    // 检查白名单权限
                    if (userInfo.in_white_list !== 1) {
                        setAuthState('no-whitelist');
                        return;
                    }

                    // 权限验证通过
                    setAuthState('authed');
                } catch (e: any) {
                    // 统一处理认证错误
                    if (e?.statusCode === 401 || e?.code === 401) {
                        setAuthState('no-auth');
                    } else if (e?.statusCode === 403 && SUB_ACCOUNT_403_MESSAGES[String(e?.code)]) {
                        setSubAccountErrCode(String(e.code));
                        setAuthState('sub-account-forbidden');
                    } else if (e?.statusCode === 403 || e?.code === 403) {
                        setAuthState('no-auth');
                    } else {
                        // 其他错误显示消息但允许继续
                        messageManager.showError(e?.message || '获取用户信息失败');
                        setAuthState('authed'); // 允许继续使用
                    }
                }
            };

            void checkAuth();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [invitecode] // 只在组件挂载时执行一次
    );

    useEffect(
        () => {
            // 只有认证通过才执行 template 初始化
            if (authState !== 'authed') {
                return;
            }

            // 判断是否需要调用 initial 接口（创建样板任务或首次登录任务）
            const needInitial = template_id || rootStore.userStore.is_first_login;
            if (!needInitial) {
                return;
            }

            // 避免重复调用：如果当前 template_id 已经处理过，跳过
            if (template_id && template_id === rootStore.userStore.template_id) {
                return;
            }

            const init = async () => {
                try {
                    setTemplateJobLoading(true);
                    await flowResult(rootStore.userStore.fetchTemplateInfo(template_id || ''));

                    // 样板间任务创建成功后，清理 URL 参数
                    if (template_id) {
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('loginstate');
                        setSearchParams(newSearchParams, {replace: true});
                    }
                } catch (e: any) {
                    // 创建失败不影响后续逻辑，继续渲染页面
                    // eslint-disable-next-line no-console
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('loginstate');
                    setSearchParams(newSearchParams, {replace: true});
                    // message.error('创建样板间任务失败: Beta版仅支持创建3个任务');
                    console.error('创建样板间任务失败:', e);
                }
                finally {
                    setTemplateJobLoading(false);
                }
            };

            void init();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            template_id,
            authState,
            rootStore.userStore.is_first_login,
            rootStore.userStore.template_id
        ]
    );

    // 初始化 MessageManager
    useEffect(
        () => {
            messageManager.setMessage(message);
            messageManager.setModal(modal);
            messageManager.setNotification(notification);
        },
        [message, modal, notification]
    );

    // 🟢 启动单页面守卫（自动关闭旧标签页）
    useEffect(
        () => {
            // 启动守卫，新标签页会自动关闭旧标签页
            singleTabGuard.start();

            // 组件卸载时停止守卫
            return () => {
                singleTabGuard.stop();
            };
        },
        []
    );

    // 企业管理员：主应用加载完成后空闲预加载子应用资源
    useEffect(
        () => {
            if (authState === 'authed' && rootStore.userStore.isEnterpriseAdmin) {
                import('@/features/enterprise/EnterpriseAdminMicroApp').then(({prefetchEnterpriseAdmin}) => {
                    prefetchEnterpriseAdmin();
                });
            }
        },
        [authState, rootStore.userStore.isEnterpriseAdmin]
    );

    function RedirectToIndex() {
        const location = useLocation();
        return <Navigate replace to={`/index${location.search}`} />;
    }

    // 权限检查中 - 显示加载
    if (authState === 'checking') {
        return (
            <div className={styles.appLoading}>
                <Spin size="large" />
            </div>
        );
    }

    // 未登录 - 跳转登录
    if (authState === 'no-auth') {
        handleUnauthorized();
        return null; // 虽然不会执行，但代码意图清晰
    }

    // 无白名单权限 - 显示白名单页面
    // if (authState === 'no-whitelist' && (location.pathname !== '/whitelist')) {
    //     return <WhitelistComponent />;
    // }

    // 企业子账号权限异常 - 展示对应提示
    if (authState === 'sub-account-forbidden') {
        return (
            <div className={styles.appLoading}>
                <Result
                    icon={<NoauthSvg />}
                    title={<span style={{fontSize: 24, color: '#fff'}}>暂无权限</span>}
                    subTitle={SUB_ACCOUNT_403_MESSAGES[subAccountErrCode] || '您暂无访问权限，请联系企业管理员。'}
                    extra={(
                        <Button type="primary" onClick={() => flowResult(rootStore.userStore.logout())}>
                            退出登录
                        </Button>
                    )}
                />
            </div>
        );
    }

    if (templateJobLoading) {
        return (
            <div className={styles.appLoading}>
                <Spin size="large" />
            </div>
        );
    }

    // 认证通过 - 渲染正常路由
    return (
        <Suspense
            fallback={
                <div className={styles.appLoading}>
                    <Spin size="large" />
                </div>
            }
        >
            <Routes>
                <Route element={<MainLayout />}>
                    <Route path="landing" element={<LandingComponent />} />
                    <Route path="*" element={<RedirectToIndex />} />
                </Route>
                <Route element={<SiderLayout rootStore={rootStore} />}>
                    <Route path="taskRunning" element={<TaskRunningComponent />} />
                    <Route path="index" element={<InitialComponent />} />
                    <Route path="billing" element={<BillingComponent />} />
                </Route>
                <Route path="admin/*" element={<EnterpriseAdminComponent />} />
                <Route path="whitelist" element={<WhitelistComponent />} />
            </Routes>
        </Suspense>
    );
});

export default AppWrapper;
